import type { TextMaxLength } from '@innspel/core';

/**
 * Leietakerkonfigen kommer ved BYGG, som `VITE_INNSPEL_*`-variabler — én
 * deployment per leietaker (tech-decisions.md, «M1.5»). Ingen av verdiene er
 * hemmelige: tenantKey er offentlig og skrivende (K3). Mangler noe påkrevd,
 * stopper portalen med feltnavnet i meldingen — aldri en stille tom side
 * («Feil bruk feiler høyt og tidlig»).
 */
export interface PortalKonfig {
  baseUrl: string;
  tenantKey: string;
  tenantName: string;
  /** Leietakerens innloggingsrute. Utelatt = kun anonym modus. */
  loginRedirectUrl?: string;
  privacyUrl: string;
  textMaxLength: TextMaxLength;
  minTextLength: number;
  screenshotsEnabled: boolean;
  theme: 'light' | 'dark';
  beta: boolean;
  /** Leietakerens setning ved bildesteget, per spor. Utelatt = ingen setning. */
  imageStepNote?: { feil?: string; onske?: string };
}

type Miljø = Record<string, string | boolean | undefined>;

export function lesKonfig(env: Miljø = import.meta.env as unknown as Miljø): PortalKonfig {
  const krev = (navn: string): string => {
    const v = env[navn];
    if (typeof v !== 'string' || v.trim() === '') {
      throw new Error(`[innspel] ${navn} mangler. Portalen bygges per leietaker med VITE_INNSPEL_*-variabler — se portal/.env.example.`);
    }
    return v.trim();
  };
  const valgfri = (navn: string): string | undefined => {
    const v = env[navn];
    return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
  };
  const bool = (navn: string): boolean => valgfri(navn)?.toLowerCase() === 'true';

  const maks = Number(valgfri('VITE_INNSPEL_TEXT_MAX_LENGTH') ?? 140);
  if (maks !== 140 && maks !== 280) {
    throw new Error('[innspel] VITE_INNSPEL_TEXT_MAX_LENGTH må være 140 eller 280 — kontrakten har to verdier.');
  }
  const min = Number(valgfri('VITE_INNSPEL_MIN_TEXT_LENGTH') ?? 10);
  if (!Number.isInteger(min) || min < 1) {
    throw new Error('[innspel] VITE_INNSPEL_MIN_TEXT_LENGTH må være et heltall ≥ 1 (DD-44).');
  }
  const tema = valgfri('VITE_INNSPEL_THEME') ?? 'light';
  if (tema !== 'light' && tema !== 'dark') {
    throw new Error('[innspel] VITE_INNSPEL_THEME må være light eller dark.');
  }

  // Setningen er leietakerens (DD-55). Den står under knappene i en smal dialog — et tak
  // hindrer at en hel erklæring havner der.
  const notat = (navn: string): string | undefined => {
    const v = valgfri(navn);
    if (v !== undefined && v.length > 280) {
      throw new Error('[innspel] ' + navn + ' er over 280 tegn. Det er én setning ved bildesteget, ikke en erklæring.');
    }
    return v;
  };
  const notatFeil = notat('VITE_INNSPEL_IMAGE_STEP_NOTE_FEIL');
  const notatOnske = notat('VITE_INNSPEL_IMAGE_STEP_NOTE_ONSKE');

  const login = valgfri('VITE_INNSPEL_LOGIN_REDIRECT_URL');
  if (login && !/^https:\/\//.test(login)) {
    throw new Error('[innspel] VITE_INNSPEL_LOGIN_REDIRECT_URL må være https — tokenet kommer tilbake i fragmentet og skal aldri gå i klartekst.');
  }

  return {
    baseUrl: krev('VITE_INNSPEL_BASE_URL'),
    tenantKey: krev('VITE_INNSPEL_TENANT_KEY'),
    tenantName: krev('VITE_INNSPEL_TENANT_NAME'),
    ...(login ? { loginRedirectUrl: login } : {}),
    privacyUrl: krev('VITE_INNSPEL_PRIVACY_URL'),
    textMaxLength: maks as TextMaxLength,
    minTextLength: min,
    screenshotsEnabled: bool('VITE_INNSPEL_SCREENSHOTS_ENABLED'),
    theme: tema,
    beta: bool('VITE_INNSPEL_BETA'),
    ...(notatFeil || notatOnske ? { imageStepNote: { ...(notatFeil ? { feil: notatFeil } : {}), ...(notatOnske ? { onske: notatOnske } : {}) } } : {}),
  };
}
